import { forwardRef, Fragment, MouseEvent, useEffect, useRef, useState } from "react";
import { Transition } from "react-transition-group";
import { PopoverUtils } from "../Popover/Popover.types.js";
import { SidebarBrand } from "./components/SidebarBrand/SidebarBrand.component.js";
import { SidebarInner } from "./components/SidebarInner/SidebarInner.component.js";
import { SidebarItem } from "./components/SidebarItem/SidebarItem.component.js";
import { SidebarSection } from "./components/SidebarSection/SidebarSection.component.js";
import { DEFAULT_TRANSITION_DURATION_MS } from "./Sidebar.constants.js";
import { SidebarContext } from "./Sidebar.context.js";
import { StyledSidebarExtraMenuContainer, StyledSidebarOuter, StyledSidebarSectionsContainer } from "./Sidebar.style.js";
import { SidebarProps, SidebarSectionDefinition } from "./Sidebar.types.js";

export const Sidebar = forwardRef<HTMLDivElement, SidebarProps>(
    (
        {
            sections,
            selected,
            open,
            brand,
            secondaryMenu,
            backgroundColor,
            floating,
            extraMenuWidth,
            onOpenChange,
            onEnteredChange,
            onEnteredChangeAfterTransition,
            onBrandClick,
            footers,
            crossSelling = false,
            ...attributes
        },
        ref,
    ): JSX.Element => {
        const [isOpen, setIsOpen] = useState<boolean>(!!open);
        const [isHovered, setIsHovered] = useState<boolean>(false);

        const crossSellPopoverRef = useRef<PopoverUtils>(null);

        const handleMouseHover = (event: MouseEvent) => {
            if (event.type === "mouseenter" && isHovered === false) {
                setIsHovered(true);
            } else if (event.type === "mouseleave" && isHovered === true) {
                setIsHovered(false);
            }
        };

        useEffect(() => {
            if (!!open !== isOpen) {
                setIsOpen(!!open);
            }
        }, [open]);

        useEffect(() => {
            if (open === undefined && isHovered && !isOpen) {
                setIsOpen(true);
            } else if (open === undefined && !isHovered && isOpen) {
                setIsOpen(false);
            }
        }, [isHovered]);

        useEffect(() => {
            if (onOpenChange && open !== isOpen) onOpenChange(isOpen);
        }, [isOpen, onOpenChange]);

        const renderSection = (section: SidebarSectionDefinition) => {
            return (
                <SidebarSection key={section.id}>
                    {section.entries &&
                        section.entries.map((entry) => {
                            const { WrapperComponent, ...rest } = entry;

                            if (WrapperComponent) {
                                return (
                                    <WrapperComponent key={entry.id}>
                                        <SidebarItem {...rest} />
                                    </WrapperComponent>
                                );
                            }

                            return <SidebarItem key={entry.id} {...rest} />;
                        })}
                    {"element" in section && <Fragment key={section.id}>{section.element}</Fragment>}
                </SidebarSection>
            );
        };

        return (
            <Transition
                in={isOpen}
                timeout={{
                    enter: 0,
                    exit: 120,
                }}
            >
                {(state) => (
                    <StyledSidebarOuter
                        ref={ref}
                        {...attributes}
                        floating={floating}
                        backgroundColor={backgroundColor}
                        extraMenuWidth={extraMenuWidth}
                        open={isOpen}
                    >
                        <SidebarContext.Provider
                            key="sidebar_provider"
                            value={{
                                open: isOpen,
                                selected,
                                transitionDuration: DEFAULT_TRANSITION_DURATION_MS,
                                transitionState: state,
                                crossSellPopoverRef,
                            }}
                        >
                            <SidebarInner
                                floating={floating}
                                open={isOpen}
                                backgroundColor={backgroundColor}
                                transitionDuration={DEFAULT_TRANSITION_DURATION_MS}
                                transitionState={state}
                                onMouseEnter={handleMouseHover}
                                onMouseLeave={handleMouseHover}
                                onEnteredChange={onEnteredChange}
                                onEnteredChangeAfterTransition={onEnteredChangeAfterTransition}
                            >
                                {brand && <SidebarBrand {...brand} crossSelling={crossSelling} onClick={onBrandClick} />}
                                <StyledSidebarSectionsContainer>{sections.map(renderSection)}</StyledSidebarSectionsContainer>
                                {footers && footers.map(renderSection)}
                            </SidebarInner>
                        </SidebarContext.Provider>
                        <StyledSidebarExtraMenuContainer floating={floating} visible={!!secondaryMenu} maxWidth={extraMenuWidth}>
                            {secondaryMenu}
                        </StyledSidebarExtraMenuContainer>
                    </StyledSidebarOuter>
                )}
            </Transition>
        );
    },
);
